import { SORT_DIRECTIONS } from 'constants/sortDirectionsEnum';
import { FieldType, GenericDataType } from 'modules/domains';
import { ListEmptyTableState, NoResult } from 'ui/illustrations';
import { spacing } from 'ui/theme';
import { pxToRem } from 'utils/convert';
import {
  CustomEventValues,
  EmptyStateConfig,
  FieldDataType,
  FieldProperty,
  FieldTableColumns,
  FieldTypeDetails,
  UserFieldValues,
} from './types';

export const DATA_SCHEMA_MANAGEMENT_URL = '/settings/dataSchemaManagement';

export const SORTING_CONTEXT_INITIAL_STATE = {
  sortBy: 'eventName',
  sortDirection: SORT_DIRECTIONS.ASCENDING,
};

export const SEARCH_PARAM_KEYS = {
  contentSection: 'contentSection',
  endDate: 'endDate',
  ingestionPolicy: 'ingestionPolicy',
  preset: 'preset',
  search: 'search',
  startDate: 'startDate',
  visibility: 'visibility',
};

export const CONTENT_SECTIONS = {
  customEvents: 'customEvents',
  purchaseFields: 'purchaseFields',
  settings: 'settings',
  userFields: 'userFields',
};

export const VISIBILITY_FILTERS = {
  all: 'all',
  hidden: 'hidden',
  visible: 'visible',
};

const actionColumn = { id: 'action', label: '' };
const createdColumn = { id: 'created', label: 'Created' };
const descriptionColumn = { id: 'description', label: 'Description' };
const nameColumn = { id: 'name', label: 'Name' };
const sampleValueColumn = { id: 'sampleValue', label: 'Sample value' };
const typeColumn = { id: 'type', label: 'Type' };

export const FIELD_TABLE_COLUMNS_WITH_BULK_ACTIONS: FieldTableColumns[] = [
  { id: 'bulkAction', label: '' },
  nameColumn,
  descriptionColumn,
  sampleValueColumn,
  {
    id: 'visibility',
    label: 'Visibility',
    justify: 'center',
    tooltipContent:
      'Hidden fields will not appear in journey filters, segmentation, or template editors.',
  },
  typeColumn,
  createdColumn,
  actionColumn,
];

export const FIELD_TABLE_COLUMNS_WITHOUTH_BULK_ACTIONS: FieldTableColumns[] = [
  nameColumn,
  descriptionColumn,
  sampleValueColumn,
  typeColumn,
  createdColumn,
  actionColumn,
];

const cellWidths = {
  action: spacing.x7,
  bulkAction: spacing.x3,
  created: pxToRem(140),
  description: `minmax(${pxToRem(100)}, 1fr)`,
  name: `minmax(${pxToRem(100)}, 1fr)`,
  sampleValue: `minmax(${pxToRem(100)}, 1fr)`,
  type: pxToRem(90),
  visibility: pxToRem(90),
};

export const FIELDT_TABLE_LAYOUT_WITHOUT_BULK_ACTIONS = `
  ${cellWidths.name}
  ${cellWidths.description}
  ${cellWidths.sampleValue}
  ${cellWidths.type}
  ${cellWidths.created}
  ${cellWidths.action}
`;

export const FIELD_TABLE_LAYOUT_WITH_BULK_ACTIONS = `
  ${cellWidths.bulkAction}
  ${cellWidths.name}
  ${cellWidths.description} 
  ${cellWidths.sampleValue}
  ${cellWidths.visibility}
  ${cellWidths.type}
  ${cellWidths.created}
  ${cellWidths.action}
`;

export const FIELD_TYPE_OPTIONS = [
  {
    id: 'boolean',
    label: 'Boolean',
    value: FieldType.BOOLEAN,
    Suffix: 'true or false',
  },
  {
    id: 'date',
    label: 'Date',
    value: FieldType.DATE,
    Suffix: '',
  },
  {
    id: 'float',
    label: 'Double',
    value: FieldType.FLOAT,
    Suffix: 'number (decimal)',
  },
  {
    id: 'geo_location',
    label: 'Geo location',
    value: FieldType.GEO_LOCATION,
    Suffix: 'lat and long',
  },
  {
    id: 'long',
    label: 'Long',
    value: FieldType.INTEGER,
    Suffix: 'number (integer)',
  },
  {
    id: 'object',
    label: 'Object',
    value: FieldType.OBJECT,
    Suffix: '',
  },
  {
    id: 'string',
    label: 'String',
    value: FieldType.STRING,
    Suffix: '',
  },
];

export const EMPTY_STATE_TEST_IDS = {
  empty: 'empty-state',
  error: 'error-state',
  noResults: 'zero-state',
};

export const PURCHASE_FIELDS_EMPTY_STATE_CONFIG: EmptyStateConfig = {
  error: {
    Illustration: NoResult,
    title: "We can't seem to find your purchase fields",
    subtitle: 'Learn more by visiting our Knowledge Center.',
  },
  // no fields are present
  empty: {
    Illustration: ListEmptyTableState,
    title: 'No user purchase fields yet',
    subtitle: 'Learn more by visiting our Knowledge Center.',
  },
  // no results based on filter(s)
  noResults: {
    Illustration: NoResult,
    title: 'No matching results',
    subtitle: 'Try adjusting your filters or searching by different keywords',
  },
};

export const USER_FIELDS_EMPTY_STATE_CONFIG: EmptyStateConfig = {
  error: {
    Illustration: NoResult,
    title: "We can't seem to find your user fields",
    subtitle: 'Learn more by visiting our Knowledge Center.',
  },
  // no fields are present
  empty: {
    Illustration: ListEmptyTableState,
    title: 'No user profile fields yet',
    subtitle: 'Learn more by visiting our Knowledge Center.',
  },
  // no results based on filter(s)
  noResults: {
    Illustration: NoResult,
    title: 'No matching results',
    subtitle: 'Try adjusting your filters or searching by different keywords',
  },
};

/**
 * Custom event field mappings
 */
export const EVENT_FIELDS_EMPTY_STATE_CONFIG: EmptyStateConfig = {
  error: {
    Illustration: NoResult,
    title: "We can't seem to find your custom event fields",
    subtitle: 'Learn more by visiting our Knowledge Center.',
  },
  // no fields are present
  empty: {
    Illustration: ListEmptyTableState,
    title: 'No event fields yet',
    subtitle: 'Learn more by visiting our Knowledge Center.',
  },
  // no results based on filter(s)
  noResults: {
    Illustration: NoResult,
    title: 'No matching results',
    subtitle: 'Try adjusting your filters or searching by different keywords',
  },
};

export const FIELD_TYPE_FILTER_OPTIONS = [
  { id: 'title', label: 'Visibility', isTitle: true },
  { id: 'visible', label: 'Visible' },
  { id: 'hidden', label: 'Hidden' },
];

/** Max character count used for name, description and sample value input fields */
export const DEFAULT_MAX_CHARACTERS = 100;

export const FIELD_PROPERTY_DETAILS: {
  [key: string]: {
    formFieldId: string;
    inputId: string;
    labelText: string;
    name: FieldProperty;
  };
} = {
  [FieldProperty.DESCRIPTION]: {
    formFieldId: 'field-description',
    inputId: 'field-description-input',
    labelText: 'Description',
    name: FieldProperty.DESCRIPTION,
  },
  [FieldProperty.SAMPLE_VALUE]: {
    formFieldId: 'field-sample-value',
    inputId: 'field-sample-value-input',
    labelText: 'Sample value',
    name: FieldProperty.SAMPLE_VALUE,
  },
};

/** Converts a GenericDataType to a FieldDataType to be used when managing Field Lock settings */
export const FIELD_TYPES_WITH_LOCK_SETTINGS: FieldTypeDetails[] = [
  {
    dataType: GenericDataType.User,
    fieldType: FieldDataType.User,
    label: 'User field',
    noun: 'fields',
  },
  {
    dataType: GenericDataType.CustomEvent,
    fieldType: FieldDataType.CustomEvent,
    label: 'Custom event',
    noun: 'events',
  },
  {
    dataType: GenericDataType.CustomEvent,
    fieldType: FieldDataType.CustomEventFields,
    label: 'Custom event field',
    noun: 'event fields',
  },
];

/** User field lock options on Settings tab */
export const userFieldsOptions = [
  {
    description: 'Unrecognized fields will be created when performing a list upload or API call.',
    id: UserFieldValues.ALLOW_USER,
    isDisabled: false,
    label: 'Allow unrecognized user fields',
    value: UserFieldValues.ALLOW_USER,
  },
  {
    description: 'Unrecognized fields will be dropped when performing a list upload or API call.',
    id: UserFieldValues.DROP_USER,
    isDisabled: false,
    label: 'Drop unrecognized user fields',
    value: UserFieldValues.DROP_USER,
  },
];

/** Custom event and field lock options on Settings tab */
export const customEventsOptions = [
  {
    description: 'Unrecognized custom events and event fields will be created automatically.',
    id: CustomEventValues.ALLOW_EVENT_AND_FIELDS,
    isDisabled: false,
    label: 'Allow unrecognized custom events and event fields',
    value: CustomEventValues.ALLOW_EVENT_AND_FIELDS,
  },
  {
    description:
      'Unrecognized custom events will be blocked, but unrecognized event fields will be created automatically.',
    id: CustomEventValues.DROP_EVENTS_ALLOW_FIELDS,
    isDisabled: false,
    label: 'Drop unrecognized custom events, allow unrecognized event fields',
    value: CustomEventValues.DROP_EVENTS_ALLOW_FIELDS,
  },
  {
    description: 'Unrecognized custom events and event fields will be dropped.',
    id: CustomEventValues.DROP_BOTH_EVENTS_AND_FIELDS,
    isDisabled: false,
    label: 'Drop unrecognized custom events and event fields',
    value: CustomEventValues.DROP_BOTH_EVENTS_AND_FIELDS,
  },
];
